import { NOTIFICATION_TYPES, type ConnectionStatus, type NotificationType } from "../types";
import type {
  ConnectionStatusHandler,
  GetTokenFn,
  HeartbeatHandler,
  NotificationSignalHandler,
  NotificationsClient,
} from "./types";

let mockSeq = 0;
function mockId(): string {
  return `mock_${Date.now().toString(36)}_${(mockSeq++).toString(36)}`;
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

/** Tipos disparados no burst inicial (variados, na ordem de chegada). */
const INITIAL_BURST: NotificationType[] = [
  "AdminTwoFactorReset",
  "AdminRoleChanged",
  "AdminAccountStatusChanged",
];

/**
 * Cliente fake. Não abre conexão real. Em desenvolvimento serve para validar a
 * UI sem depender do stream SSE do serviço central — emite sinais SLIM sintéticos
 * (`{id,type,unread}`, contrato option C / ADR-0025) em intervalos variáveis (com
 * um burst inicial logo após start) e dispara heartbeats periódicos. A lista do
 * sino é populada pelo mock REST (`api/mock.ts`) via `loadHistory()`; aqui só
 * disparamos os sinais e a contagem de não lidas fake.
 */
class MockNotificationsClient implements NotificationsClient {
  private status: ConnectionStatus = "disconnected";
  private statusHandlers: ConnectionStatusHandler[] = [];
  private notificationHandlers: NotificationSignalHandler[] = [];
  private heartbeatHandlers: HeartbeatHandler[] = [];
  private burstTimers: number[] = [];
  private recurringTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private fakeUnread = 0;
  private built = false;

  build(_getToken: GetTokenFn): void {
    void _getToken;
    this.built = true;
  }

  async start(): Promise<void> {
    if (!this.built) {
      throw new Error("Mock client not built. Call build() first.");
    }
    this.fakeUnread = 0;
    this.emitStatus("reconnecting");
    // Simula latência de handshake
    await new Promise((r) => setTimeout(r, 400));
    this.emitStatus("connected");

    // Burst inicial: dispara alguns sinais ao longo dos primeiros ~6s.
    INITIAL_BURST.forEach((type, i) => {
      const t = window.setTimeout(() => this.dispatchSignal(type), 1500 * (i + 1));
      this.burstTimers.push(t);
    });

    // Recorrente: a cada 20–40s manda 1 sinal aleatório.
    this.scheduleNextRecurring();
    // Heartbeat: a cada ~25s, igual ao servidor real, cura o badge.
    this.scheduleNextHeartbeat();
  }

  async stop(): Promise<void> {
    this.burstTimers.forEach((t) => window.clearTimeout(t));
    this.burstTimers = [];
    if (this.recurringTimer !== null) {
      window.clearTimeout(this.recurringTimer);
      this.recurringTimer = null;
    }
    if (this.heartbeatTimer !== null) {
      window.clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.emitStatus("disconnected");
  }

  onNotification(handler: NotificationSignalHandler): void {
    this.notificationHandlers.push(handler);
  }

  onHeartbeat(handler: HeartbeatHandler): void {
    this.heartbeatHandlers.push(handler);
  }

  getConnectionState(): ConnectionStatus {
    return this.status;
  }

  onStatusChange(handler: ConnectionStatusHandler): void {
    this.statusHandlers.push(handler);
  }

  private dispatchSignal(type: NotificationType): void {
    this.fakeUnread += 1;
    const signal = { id: mockId(), type, unread: this.fakeUnread };
    for (const h of this.notificationHandlers) {
      try {
        h(signal);
      } catch (err) {
        console.error("[sse-mock] notification handler threw:", err);
      }
    }
  }

  private emitHeartbeat(): void {
    for (const h of this.heartbeatHandlers) {
      try {
        h(this.fakeUnread);
      } catch (err) {
        console.error("[sse-mock] heartbeat handler threw:", err);
      }
    }
  }

  private scheduleNextRecurring(): void {
    const delay = 20_000 + Math.random() * 20_000;
    this.recurringTimer = window.setTimeout(() => {
      if (this.status === "connected") {
        this.dispatchSignal(pick(NOTIFICATION_TYPES));
      }
      this.scheduleNextRecurring();
    }, delay);
  }

  private scheduleNextHeartbeat(): void {
    this.heartbeatTimer = window.setTimeout(() => {
      if (this.status === "connected") this.emitHeartbeat();
      this.scheduleNextHeartbeat();
    }, 25_000);
  }

  private emitStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const h of this.statusHandlers) {
      try {
        h(status);
      } catch (err) {
        console.error("[sse-mock] status handler threw:", err);
      }
    }
  }
}

export const mockNotificationsClient: NotificationsClient = new MockNotificationsClient();
